/**
 * Fixes for the top 4 JS errors flagged in Microsoft Clarity.
 * Vanilla JS, no dependencies. Add as /assets/clarity-js-error-fixes.js
 * and load it FIRST in theme.liquid, right after <head>:
 *
 *   <script src="{{ 'clarity-js-error-fixes.js' | asset_url }}"></script>
 *
 * It must load before any pixels/apps (Meta, TikTok, review widgets, etc.)
 * for fix #1 to work.
 */

(function () {
  "use strict";

  /* ------------------------------------------------------------------
   * 1. "undefined is not an object (evaluating 'window.webkit.messagehandlers')"
   *    43.86% of errors.
   *    Means `window.webkit` is undefined and some third-party script
   *    (a pixel, chat widget, review app, etc.) calls
   *    window.webkit.messageHandlers.<name>.postMessage(...) assuming it's
   *    running inside an iOS WKWebView, without checking first. On regular
   *    Safari/Chrome, window.webkit doesn't exist, so it throws.
   *    Fix: stub window.webkit.messageHandlers so any handler name/method
   *    resolves to a harmless no-op instead of throwing.
   * ------------------------------------------------------------------ */
  if (typeof window.webkit === "undefined") {
    var noopHandler = { postMessage: function () {} };
    window.webkit = {
      messageHandlers: new Proxy(
        {},
        {
          get: function () {
            return noopHandler;
          },
        }
      ),
    };
  }

  /* ------------------------------------------------------------------
   * 2. "ResizeObserver loop completed with undelivered notifications"
   *    20.18% of errors.
   *    Harmless browser warning (not a real bug) triggered when a
   *    ResizeObserver callback itself changes size synchronously
   *    (common with sliders/carousels, sticky headers, lazy-load libs).
   *    Fix: defer every ResizeObserver callback to the next frame so it
   *    never fires within the same layout pass, and silence the message
   *    if it still surfaces so it stops polluting error tracking.
   * ------------------------------------------------------------------ */
  if (typeof window.ResizeObserver !== "undefined") {
    var NativeResizeObserver = window.ResizeObserver;
    window.ResizeObserver = function (callback) {
      return new NativeResizeObserver(function (entries, observer) {
        window.requestAnimationFrame(function () {
          callback(entries, observer);
        });
      });
    };
    window.ResizeObserver.prototype = NativeResizeObserver.prototype;
  }

  window.addEventListener(
    "error",
    function (e) {
      if (e.message && e.message.indexOf("ResizeObserver loop") !== -1) {
        e.stopImmediatePropagation();
      }
    },
    true
  );

  /* ------------------------------------------------------------------
   * 3. "Script error." (no file/line/column, no stack)
   *    14.04% of errors.
   *    The browser hides real error details for scripts loaded
   *    cross-origin without a `crossorigin` attribute + matching CORS
   *    header. Applies to any external JS you host yourself (e.g. on
   *    Shopify CDN via asset_url, or a separate domain/CDN you control).
   *
   *    Fix, for every self-hosted external <script> tag in your theme:
   *
   *      <script src="{{ 'my-file.js' | asset_url }}" crossorigin="anonymous"></script>
   *
   *    and make sure that file's host returns:
   *      Access-Control-Allow-Origin: *
   *
   *    Third-party app scripts you don't control (apps, pixels) will
   *    keep throwing generic "Script error." — that's expected and not
   *    actionable, so it's fine to filter those out of error tracking
   *    rather than treat them as real bugs.
   * ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------
   * 4. "NetworkError: Load failed" / "TypeError: Failed to fetch"
   *    8.77% of errors.
   *    Usually a fetch() call (cart AJAX, analytics beacon, app API
   *    call) that gets aborted because the user navigated away before
   *    it finished, or blocked by an ad blocker/browser extension.
   *    Fix: wrap fetch calls used for "fire and forget" tracking so a
   *    failed/aborted request never surfaces as an unhandled error.
   *    Use this helper in place of a bare fetch() for that use case.
   * ------------------------------------------------------------------ */
  window.safeFetch = function (url, options) {
    return fetch(url, options).catch(function (err) {
      // Swallow aborted/blocked network calls — nothing actionable here.
      console.debug("safeFetch: request failed silently", url, err);
    });
  };

  // Also catch any fetch-related rejections that aren't wrapped yet,
  // so they don't bubble up as unhandled promise rejections.
  window.addEventListener("unhandledrejection", function (e) {
    var msg = e.reason && e.reason.message ? e.reason.message : "";
    if (msg.indexOf("Load failed") !== -1 || msg.indexOf("Failed to fetch") !== -1) {
      e.preventDefault();
    }
  });
})();